import type { ReactNode } from 'react'

import { BodyL, FlowPage } from '@monthio/design-system'
import { CommentInputField, Region } from '@monthio/react-components'
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { BudgetType } from '../../types/authenticateSmartcheckSessionDto'
import { BudgetOverview } from './Components/BudgetOverview'
import { budgetEstimatesCalculatorService } from './Components/BudgetOverview/budgetEstimatesCalculatorService'
import { generateEstimatesWithBudgetAsTree } from './Components/BudgetOverview/budgetEstimatesUtils'
import { DocumentsUploader } from './Components/documentsUploader'
import { FinishButtonSection } from './Components/finishButtonSection'
import { HistoricalOverviewIntro } from './Components/HistoricalOverviewIntro'
// import { GoToBankSelectorButton } from './Components/GoToBankSelectorButton'
import {
	isBudgetValueValid,
	MissingBudgetInfo,
	MissingInfoBudgetKeys,
} from './Components/MissingBudgetInfo'
import { useBudgetModule } from './useBudgetModule'

const StyledText = styled.span`
	font-weight: bolder;
	font-style: italic;
`

interface ConsumerMiniBudgetFormData {
	userNote: string | null
}

export const Budget = () => {
	const {
		config: { budgetType },
	} = useBudgetModule()
	const [
		shouldShowHistoricalOverviewIntro,
		setShouldShowHistoricalOverviewIntro,
	] = useState(budgetType === BudgetType.V2LastYearAverages)

	if (shouldShowHistoricalOverviewIntro) {
		return (
			<HistoricalOverviewIntro
				onCompleted={() => setShouldShowHistoricalOverviewIntro(false)}
			/>
		)
	}

	return <Page />
}

const Page = () => {
	const { data, finish, config } = useBudgetModule()

	const budgetEstimates = generateEstimatesWithBudgetAsTree(
		(data.userCorrected as any) ?? data.estimates,
	)
	const { t } = useTranslation()
	const [files, setFiles] = useState<File[]>([])
	const [missingInfoConfirmed, setMissingInfoConfirmed] = useState(false)
	const [miniBudgetFormData, setMiniBudgetFormData] =
		useState<ConsumerMiniBudgetFormData>({ userNote: null })

	const [areInitialEstimatesSet, setAreInitialEstimatesSet] = useState(false)

	useEffect(() => {
		if (budgetEstimates && !areInitialEstimatesSet) {
			setAreInitialEstimatesSet(true)
			budgetEstimatesCalculatorService.setInitial(budgetEstimates)
		}
	}, [budgetEstimates])

	const handleMissingBudgetInfoChange = () => setMissingInfoConfirmed(true)

	const submitData = async () => {
		await finish({
			formData: miniBudgetFormData,
			estimates:
				budgetEstimatesCalculatorService.getEstimatesForSubmit() as any,
			uploadedFiles: files,
		})
	}

	const buttons: ReactNode[] = [
		// <GoToBankSelectorButton key="goToBankSelectorButton" />,
		<FinishButtonSection
			key="finishButtonSection"
			shouldUseBudgetHelper={config.shouldUseBudgetHelper}
			onClick={submitData}
		/>,
	]

	const title =
		config.budgetType === BudgetType.V2LastYearAverages
			? t('miniBudgetForm-historicalAveragesHeader-title')
			: t('miniBudgetForm-header-title')

	const subtitle = config.partOfMultiFlow
		? t('miniBudgetForm-header-paired-applicantion-body')
		: config.budgetType === BudgetType.V2LastYearAverages
			? t('miniBudgetForm-historicalAveragesHeader-body')
			: t('miniBudgetForm-header-body')

	if (!areInitialEstimatesSet) {
		return null
	}

	const showMissingInfo = () => {
		// The MissingBudgetInfo component has hardcoded values like salary and rent thresholds for the data to be
		// considered missing. These numbers will not make sense in another currency like EUR
		// TODO: Refactor this component. https://monthio.atlassian.net/browse/PT-4840
		if (
			(config.regionId !== Region.Denmark &&
				config.regionId !== Region.Sweden) ||
			missingInfoConfirmed
		) {
			return false
		}

		for (const key of Object.values(MissingInfoBudgetKeys)) {
			if (!isBudgetValueValid(key, budgetEstimates, true, config.budgetType)) {
				return true
			}
		}
	}

	if (showMissingInfo()) {
		return (
			<MissingBudgetInfo
				budgetEstimates={budgetEstimates}
				miniBudgetType={config.budgetType}
				submitMissingBudgetInfo={handleMissingBudgetInfoChange}
			/>
		)
	}

	return (
		<FlowPage>
			<FlowPage.Content $spacing="xl">
				<FlowPage.Header title={title} description={subtitle} />
				<div>
					<BudgetOverview
						historicalAverage={
							config.budgetType === BudgetType.V2LastYearAverages
						}
					/>
					{config.shouldShowConsumerNote && (
						<CommentInputField
							rows={4}
							maxLength={1000}
							placeholder={t('minibudgetForm-commentSection-text')}
							errorMessage={t('budgetForm-comment-error')}
							value={miniBudgetFormData.userNote || ''}
							handleChange={(event: React.ChangeEvent<any>) =>
								setMiniBudgetFormData({
									...miniBudgetFormData,
									userNote: event.target.value,
								})
							}
						/>
					)}
					{config.shouldAllowFileUpload ? (
						<DocumentsUploader
							handleChange={(files: any) => setFiles(files)}
							uploadedFiles={files}
						/>
					) : null}
				</div>
				<BodyL>
					<Trans
						i18nKey={'minibudgetForm-finishSection-text'}
						values={{
							clientName: config.clientName ?? t('leasingOrLoanProvider-text'),
						}}
						components={{
							clientNameText: <StyledText />,
						}}
					/>
				</BodyL>
			</FlowPage.Content>
			<FlowPage.Footer actions={buttons} />
		</FlowPage>
	)
}
